import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Eye, EyeOff, FileText } from 'lucide-react';
import api from '../../api';

/**
 * Registre des introductions — read-only audit log.
 * Records every identity disclosure (or hide/undo) with client, project, manufacturer,
 * mandate version, date, and admin user.
 */
export default function DisclosureRegistry() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/disclosures');
      setItems(res.data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="disclosure-registry">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand-navy" />
          <h2 className="font-bold text-brand-navy">Registre des introductions</h2>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-bold">{items.length}</span>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
          <RefreshCw className="w-3.5 h-3.5" /> Recharger
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">Journal permanent des divulgations d'identité de manufacturiers. Consigne le client, le projet, le mandat applicable et l'administrateur ayant effectué la révélation.</p>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-gray-400 italic text-sm">Aucune divulgation enregistrée pour le moment.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead>
              <tr className="text-left border-b-2 border-gray-200 text-brand-navy">
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Date</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Action</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Manufacturier</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Code anonyme</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Client</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Projet</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Mandat v.</th>
                <th className="py-2 pr-3 font-bold uppercase tracking-wider text-[10px]">Par</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-gray-100 align-top" data-testid={`disclosure-row-${i.id}`}>
                  <td className="py-2 pr-3 text-gray-700">{i.revealedAt ? new Date(i.revealedAt).toLocaleString('fr-CA') : '—'}</td>
                  <td className="py-2 pr-3">
                    {i.action === 'reveal' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300 font-bold uppercase"><Eye className="w-3 h-3" /> Révélée</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-bold uppercase"><EyeOff className="w-3 h-3" /> Masquée</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-semibold text-brand-navy">{i.manufacturerName || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-gray-600">{i.anonymousCode || '—'}</td>
                  <td className="py-2 pr-3 text-brand-navy">{i.clientName || '—'}<div className="text-[10px] text-gray-500">{i.clientCompany}</div></td>
                  <td className="py-2 pr-3 font-mono text-gray-500">{(i.rfqId || '').slice(0, 8)}</td>
                  <td className="py-2 pr-3 text-gray-600">v{i.mandateVersion || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600">{i.revealedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
